/**
 * Enhanced Map Provider Component
 * Manages map instance lifecycle and provides context to children
 */
import type { MutableRefObject } from "react";
import { createContext, useMemo } from "react";

import type { Map, PaddingOptions } from "maplibre-gl";
import type { Feature, FeatureCollection, Geometry } from "geojson";

import { useMapboxDraw } from "@/hooks/map/core/useMapboxDraw";
import { useMapInitialization } from "@/hooks/map/core/useMapInitialization";
import { useMapLayerInitialization } from "@/hooks/map/core/useMapLayerInitialization";
import useAdminArea from "@/hooks/map/features/useAdminArea";
import useCatchmentArea from "@/hooks/map/features/useCatchmentArea";
import { useGlobalAdminArea } from "@/hooks/map/features/useGlobalAdminArea";
import useMapMarkers from "@/hooks/map/features/useMapMarkers";
import useFeatureSelection from "@/hooks/map/interactions/useFeatureSelection";
import useLegendFilter from "@/hooks/map/interactions/useLegendFilter";
import useMapInteractions from "@/hooks/map/interactions/useMapInteractions";
import useGridLayers from "@/hooks/map/layers/useGridLayers";
import useH3GeoHashLayers from "@/hooks/map/layers/useH3GeoHashLayer";
import { useLineLayers } from "@/hooks/map/layers/useLineLayers";
import useMapLayers from "@/hooks/map/layers/useMapLayers";
import usePointLayers from "@/hooks/map/layers/usePointLayers";
import usePolygonLayers from "@/hooks/map/layers/usePolygonLayers";
import useSiteProfilingLayers from "@/hooks/map/layers/useSiteProfilingLayers";
import useMapStyles from "@/hooks/map/styling/useMapStyles";
import { LegendFilterType } from "@/store/slice/mapSlice";
import type { AdminAreaData } from "@/types/adminArea";
import type { SpatialAggregationType } from "@/types/common";
import type {
  DataSchemaType,
  Dataset,
  DatasetAttributeSchema,
  DatasetData,
  DatasetStyle,
  EColorLabel,
  EColorNames
} from "@/types/dataset";
import type {
  MapStyleProperties,
  MarkerPinItem,
  SelectedFeature
} from "@/types/map";
import { PlaceItem } from "@/types/place";

export const DEFAULT_ZOOM = 4;
// Type guard to narrow down the type

export type MapContextType = {
  map: Map | null;
  popup: maplibregl.Popup | null;
  zoomLevel: number;
  currentCenter: [number, number];
  setContainer: (element: HTMLDivElement) => void;
  baseMapRef: MutableRefObject<string | maplibregl.StyleSpecification | null>;
  addFeature: (feature: Feature) => string | string[] | undefined;
  enterDrawMode: () => void;
  exitDrawMode: () => void;
  enterEditMode: (featureId: string) => void;
  enterSelectMode: () => void;
  renderDrawPolygon: () => void;
  deleteAllFeatures: () => void;
  synchronizeFeatures: () => void;
  deleteFeature: (featureId: string) => void;
  drawMode: string;
  handleClearSelectedState: (feature: SelectedFeature) => void;
  handleHighlightGridSelection: (sourceId: string, gridIds: string[]) => void;
  handleAddPointsLayer: (
    dataset: Dataset,
    pointsData: FeatureCollection<Geometry>,
    customId?: string,
    filteredData?: FeatureCollection<Geometry>,
    beforeId?: string
  ) => void;
  handleAddPolygonLayer: (
    dataset: Dataset,
    data: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
  handleAddH3GeohashLayer: (
    dataset: Dataset,
    data: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
  handleAddLineLayer: (
    dataset: Dataset,
    lineData: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
  handleResetDatasetToHistoryDataset: () => void;
  handleAddGridLayer: (
    dataset: Dataset,
    data: FeatureCollection<Geometry>,
    prefix?: string
  ) => void;
  handleAddSiteProfiling: (
    newDataset: Dataset,
    featureCollection: FeatureCollection<Geometry>
  ) => void;
  removeAllCatchmentMarkers: () => void;
  removeAllMarkers: () => void;
  handleAddCatchmentLayer: (
    dataset: Dataset,
    polygonData: FeatureCollection<Geometry>
  ) => void;
  handleChangeColorDataLayers: (
    dataset: Dataset,
    selectedCategory: DatasetAttributeSchema,
    colorFamily: EColorNames,
    step: number,
    prefix?: string
  ) => void;
  handleChangeColorLayers: (
    datasetId: string,
    colorFamily: EColorNames,
    colorRanges: {
      min?: number | null;
      max?: number | null;
      value?: string | null;
      color: string;
      label: string;
    }[],
    selectedCategoryName: string,
    spatialType: SpatialAggregationType,
    dataType: DataSchemaType,
    step?: number
  ) => void;
  handleChangeVisibleLabelLayer: (
    layerId: string,
    visibleLayer: boolean
  ) => void;
  handleDisplayNameLabelLayerCategory: (
    layerId: string,
    selectedCategory: DatasetAttributeSchema
  ) => void;
  handleChangeLabelLayerColor: (
    layerId: string,
    selectedColor: EColorLabel
  ) => void;
  handleChangeLabelLayerOpacity: (layerId: string, opacity: number) => void;
  showOnlyLayer: (layerIdToKeepVisible: string) => void;
  restoreAllLayersVisibility: () => void;
  hideLayersByIds: (layerIdsToHide: string[]) => void;
  handleMapFullScreen: () => void;
  hideAllMarkers: () => void;
  showAllMarkers: () => void;
  handleResetLayerOrder: (layerOrders: string[]) => void;
  handleResetLayerData: (
    datasets: { [id: string]: Dataset },
    rawDatasets: { [id: string]: DatasetData }
  ) => void;
  handleHideAllLayerVisibility: () => void;
  handleRemoveMarker: (index: number, item: MarkerPinItem) => void;
  hideAllMarkerLabels: () => void;
  showAllMarkerLabels: () => void;
  handleStyleRankEnabledChange: (value: {
    datasetId: string;
    isEnabled: boolean;
    isShowLabel: boolean;
  }) => void;
  handleUpdateSourceWithRank: (value: {
    datasetId: string;
    style: DatasetStyle;
    update: {
      isEnabled?: boolean;
      maxRanking?: number;
      category?: DatasetAttributeSchema;
    };
  }) => Promise<void>;

  handleRemoveSourceLayer: (
    datasetId: string,
    spatialType: SpatialAggregationType
  ) => void;
  handleAddRegionLayer: (
    sourceId: string,
    polygonData: FeatureCollection<Geometry>,
    styleProperties?: MapStyleProperties
  ) => void;
  handleAddIsochroneLayer: (
    sourceId: string,
    polygonData: FeatureCollection<Geometry>,
    styleProperties?: MapStyleProperties
  ) => void;
  handleChangeOpacityLayers: (
    datasetId: string,
    opacity: number,
    spatialType: SpatialAggregationType
  ) => void;
  handleFilterLayers: (datasetId: string) => void;
  handleMovePositionToLayer: (
    polygonData: FeatureCollection<Geometry>,
    options?: {
      padding?: PaddingOptions | number;
      offset?: [number, number]; // [x, y] offset in pixels
      zoom?: number; // Custom zoom level
    }
  ) => void;
  handleChangeVisibility: (
    datasetId: string,
    visibility: "visible" | "none",
    spatialType: SpatialAggregationType,
    showLabel: boolean,
    isRankEnabled: boolean
  ) => void;
  fitMapToLayerBound: (geojson: AdminAreaData) => void;
  handleAddMarker: (
    id: string,
    coordinates: [number, number],
    data: PlaceItem | string | null
  ) => void;
  handleRemoveMarkerById: (id: string) => void;
  handleFilterLayerByLegend: (
    newfilterLegends: LegendFilterType[],
    data: Dataset,
    rawDataset: DatasetData,
    beforeId?: string
  ) => void;
  handleHighlightFeatureByGeometry: (
    datasetId: string,
    geometry: Geometry
  ) => void;
  resetAllFeatureStates: () => void;
  resetMapView: () => void;
  handleClearHighlightedGrids: (sourceId: string | null) => void;
};

export interface H3Feature extends Feature {
  properties: {
    h3Index: string;
    value: number;
    resolution: number;
    center: number[];
  };
}

export const MapContext = createContext<MapContextType>({
  map: null,
  popup: null,
  zoomLevel: DEFAULT_ZOOM,
  currentCenter: [0, 0],
  baseMapRef: {
    current: null
  },
  setContainer: () => {},
  handleClearSelectedState: () => {},
  handleHighlightGridSelection: () => {},
  addFeature: () => "",
  enterDrawMode: () => {},
  exitDrawMode: () => {},
  enterEditMode: () => {},
  enterSelectMode: () => {},
  deleteFeature: () => {},
  synchronizeFeatures: () => {},
  renderDrawPolygon: () => {},
  drawMode: "",
  deleteAllFeatures: () => {},
  removeAllMarkers: () => {},
  hideAllMarkers: () => {},
  showAllMarkers: () => {},
  showOnlyLayer: () => {},
  restoreAllLayersVisibility: () => {},
  hideLayersByIds: () => {},
  handleHideAllLayerVisibility: () => {},
  removeAllCatchmentMarkers: () => {},
  handleResetDatasetToHistoryDataset: () => {},
  handleAddGridLayer: () => {},
  handleAddSiteProfiling: () => {},
  handleAddPointsLayer: () => {},
  handleAddH3GeohashLayer: () => {},
  handleAddPolygonLayer: () => {},
  handleAddLineLayer: () => {},
  handleChangeColorDataLayers: () => {},
  handleChangeColorLayers: () => {},
  handleMapFullScreen: () => {},
  handleRemoveMarker: () => {},
  handleRemoveSourceLayer: () => {},
  handleAddRegionLayer: () => {},
  handleAddIsochroneLayer: () => {},
  handleChangeOpacityLayers: () => {},
  handleAddCatchmentLayer: () => {},
  handleResetLayerOrder: () => {},
  handleResetLayerData: () => {},
  handleFilterLayers: () => {},
  handleMovePositionToLayer: () => {},
  handleChangeVisibility: () => {},
  fitMapToLayerBound: () => {},
  handleAddMarker: () => {},
  handleRemoveMarkerById: () => {},
  handleFilterLayerByLegend: () => {},
  resetMapView: () => {},
  hideAllMarkerLabels: () => {},
  showAllMarkerLabels: () => {},
  handleChangeVisibleLabelLayer: () => {},
  handleDisplayNameLabelLayerCategory: () => {},
  handleChangeLabelLayerColor: () => {},
  handleChangeLabelLayerOpacity: () => {},
  handleStyleRankEnabledChange: () => {},
  handleUpdateSourceWithRank: async () => {},
  handleHighlightFeatureByGeometry: () => {},
  resetAllFeatureStates: () => {},
  handleClearHighlightedGrids: () => {}
});

export const MapProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    isMapLoaded,
    map,
    baseMapRef,
    zoomLevel,
    setContainer,
    handleMapFullScreen,
    currentCenter,
    popup
  } = useMapInitialization();

  const {
    enterDrawMode,
    exitDrawMode,
    enterEditMode,
    deleteFeature,
    deleteAllFeatures,
    synchronizeFeatures,
    renderDrawPolygon,
    enterSelectMode,
    addFeature,
    drawMode
  } = useMapboxDraw(map, popup);

  const {
    handleClearSelectedState,
    handleHighlightGridSelection,
    handleClearHighlightedGrids
  } = useFeatureSelection(map!);

  const {
    prepareLayerStyles,
    handleChangeOpacityLayers,
    handleChangeColorLayers,
    handleChangeColorDataLayers,
    handleFilterLayers,
    handleChangeVisibility,
    handleChangeVisibleLabelLayer,
    handleDisplayNameLabelLayerCategory,
    handleChangeLabelLayerColor,
    handleChangeLabelLayerOpacity,
    handleStyleRankEnabledChange,
    handleUpdateSourceWithRank
  } = useMapStyles(map!);

  const {
    handleHighlightFeatureByGeometry,
    handleFeatureClick,
    handleMovePositionToLayer,
    fitMapToLayerBound,
    resetAllFeatureStates,
    resetMapView
  } = useMapInteractions({
    map: map!,
    handleClearSelectedState
  });

  // Create adapter for 3-parameter version of prepareLayerStyles
  const prepareLayerStylesAdapter = (
    parsedPolygonData: FeatureCollection<Geometry>,
    dataset: Dataset,
    step: number
  ) => {
    return prepareLayerStyles(
      parsedPolygonData,
      dataset,
      dataset.selectedCategory,
      step
    );
  };

  const { handleAddGridLayer } = useGridLayers({
    map: map!,
    handleFeatureClick,
    handleMovePositionToLayer,
    prepareLayerStyles: prepareLayerStylesAdapter,
    handleUpdateSourceWithRank
  });

  const { handleAddPointsLayer } = usePointLayers({
    map: map!,
    handleFeatureClick,
    handleMovePositionToLayer,
    handleUpdateSourceWithRank
  });

  const { handleAddPolygonLayer } = usePolygonLayers({
    map: map!,
    prepareLayerStyles,
    handleFeatureClick,
    handleMovePositionToLayer,
    handleUpdateSourceWithRank
  });

  const { handleAddH3GeohashLayer } = useH3GeoHashLayers({
    map: map!,
    prepareLayerStyles,
    handleFeatureClick,
    handleMovePositionToLayer,
    handleUpdateSourceWithRank
  });

  const {
    markers,
    handleAddMarkers,
    handleRemoveMarker,
    removeAllCatchmentMarkers,
    removeAllMarkers,
    hideAllMarkers,
    showAllMarkers,
    hideAllMarkerLabels,
    showAllMarkerLabels,
    handleAddMarker,
    handleRemoveMarkerById
  } = useMapMarkers({
    map: map!,
    isMapLoaded
  });

  const { handleAddSiteProfiling } = useSiteProfilingLayers({
    handleAddPolygonLayer,
    handleAddMarkers,
    hideAllMarkers
  });

  const { handleAddLineLayer } = useLineLayers({
    map: map!,
    prepareLayerStyles,
    handleFeatureClick,
    handleMovePositionToLayer,
    handleUpdateSourceWithRank
  });

  const {
    handleAddCatchmentLayer,
    handleAddRegionLayer,
    removeCatchmentMapDisplay,
    handleAddIsochroneLayer
  } = useCatchmentArea({
    map: map!,
    isMapLoaded,
    removeAllMarkers,
    handleAddPolygonLayer,
    handleAddMarkers
  });

  const {
    handleClearAnalysisResultView,
    handleResetDatasetToHistoryDataset,
    handleRemoveSourceLayer,
    handleResetLayerOrder,
    handleHideAllLayerVisibility,
    handleResetLayerData,
    showOnlyLayer,
    restoreAllLayersVisibility,
    hideLayersByIds
  } = useMapLayers({
    map: map!,
    removeAllMarkers,
    handleAddPointsLayer,
    handleAddPolygonLayer,
    handleAddH3GeohashLayer,
    handleAddLineLayer
  });

  useAdminArea({
    map: map!,
    isMapLoaded,
    markers,
    zoomLevel,
    removeCatchmentMapDisplay,
    handleAddRegionLayer,
    removeAllMarkers
  });

  useGlobalAdminArea({
    map: map!,
    isMapLoaded,
    zoomLevel
  });

  useMapLayerInitialization({
    map: map!,
    isMapLoaded,
    handleAddLineLayer,
    handleAddSiteProfiling,
    handleAddPolygonLayer,
    handleAddPointsLayer,
    handleAddH3GeohashLayer,
    handleClearAnalysisResultView,
    handleClearSelectedState,
    removeCatchmentMapDisplay
  });

  const { handleFilterLayerByLegend } = useLegendFilter({
    map: map!,
    handleAddPointsLayer,
    handleRemoveSourceLayer,
    handleClearSelectedState
  });

  const contextValue = useMemo(
    () => ({
      map: isMapLoaded && map ? map : null,
      popup,
      zoomLevel,
      currentCenter,
      baseMapRef,
      addFeature,
      renderDrawPolygon,
      deleteAllFeatures,
      synchronizeFeatures,
      deleteFeature,
      enterDrawMode,
      exitDrawMode,
      enterEditMode,
      enterSelectMode,
      drawMode,
      setContainer,
      hideAllMarkers,
      showAllMarkers,
      showOnlyLayer,
      hideLayersByIds,
      handleMapFullScreen,
      handleChangeColorLayers,
      handleHideAllLayerVisibility,
      handleAddCatchmentLayer,
      removeAllCatchmentMarkers,
      handleClearSelectedState,
      handleHighlightGridSelection,
      handleAddIsochroneLayer,
      handleResetLayerOrder,
      handleResetLayerData,
      handleChangeOpacityLayers,
      handleChangeColorDataLayers,
      handleAddGridLayer,
      handleAddPolygonLayer,
      handleAddH3GeohashLayer,
      handleAddPointsLayer,
      handleAddLineLayer,
      handleChangeVisibleLabelLayer,
      handleDisplayNameLabelLayerCategory,
      handleChangeLabelLayerOpacity,
      handleChangeLabelLayerColor,
      handleRemoveSourceLayer,
      handleRemoveMarker,
      handleAddRegionLayer,
      handleResetDatasetToHistoryDataset,
      removeAllMarkers,
      handleMovePositionToLayer,
      handleFilterLayers,
      handleChangeVisibility,
      handleAddSiteProfiling,
      fitMapToLayerBound,
      handleAddMarker,
      handleRemoveMarkerById,
      handleFilterLayerByLegend,
      resetMapView,
      hideAllMarkerLabels,
      showAllMarkerLabels,
      handleStyleRankEnabledChange,
      handleUpdateSourceWithRank,
      handleHighlightFeatureByGeometry,
      resetAllFeatureStates,
      restoreAllLayersVisibility,
      handleClearHighlightedGrids
    }),
    [
      isMapLoaded,
      map,
      popup,
      zoomLevel,
      currentCenter,
      baseMapRef,
      setContainer,
      hideAllMarkers,
      showAllMarkers,
      addFeature,
      renderDrawPolygon,
      synchronizeFeatures,
      deleteAllFeatures,
      deleteFeature,
      enterDrawMode,
      exitDrawMode,
      enterEditMode,
      enterSelectMode,
      drawMode,
      showOnlyLayer,
      hideLayersByIds,
      handleMapFullScreen,
      handleChangeColorLayers,
      handleHideAllLayerVisibility,
      handleAddCatchmentLayer,
      removeAllCatchmentMarkers,
      handleClearSelectedState,
      handleHighlightGridSelection,
      handleAddIsochroneLayer,
      handleResetLayerOrder,
      handleResetLayerData,
      handleChangeOpacityLayers,
      handleChangeColorDataLayers,
      handleAddGridLayer,
      handleAddPolygonLayer,
      handleAddPointsLayer,
      handleAddLineLayer,
      handleChangeVisibleLabelLayer,
      handleDisplayNameLabelLayerCategory,
      handleChangeLabelLayerOpacity,
      handleChangeLabelLayerColor,
      handleAddH3GeohashLayer,
      handleRemoveSourceLayer,
      handleRemoveMarker,
      handleAddRegionLayer,
      handleResetDatasetToHistoryDataset,
      removeAllMarkers,
      handleMovePositionToLayer,
      handleFilterLayers,
      handleChangeVisibility,
      handleAddSiteProfiling,
      fitMapToLayerBound,
      handleAddMarker,
      handleRemoveMarkerById,
      handleFilterLayerByLegend,
      resetMapView,
      hideAllMarkerLabels,
      showAllMarkerLabels,
      handleStyleRankEnabledChange,
      handleUpdateSourceWithRank,
      handleHighlightFeatureByGeometry,
      resetAllFeatureStates,
      restoreAllLayersVisibility,
      handleClearHighlightedGrids
    ]
  );

  return (
    <MapContext.Provider value={contextValue}>{children}</MapContext.Provider>
  );
};
